<template>
  <div>
    <!-- Back & Edit Button -->
    <app-bar class="d-flex flex-row mb-1">
      <back-button />
      <div class="flex-grow-1"></div>
      <small-circle-button
        :iconClass="!isEditProfile ? 'bi bi-pencil-fill' : 'bi bi-x-lg'"
        :cssClass="isEditProfile ? 'scss-button-red' : ''"
        @click="toggleEditProfile()"
      />
    </app-bar>

    <list-header title="My Profile" />
    <Form
      @submit="handleEditProfile()"
      :validation-schema="schema"
      class="mt-4 text-start"
      ref="profileForm"
    >
      <!-- First Name -->
      <div class="ms-4 me-4">
        First Name<span class="required">*</span>

        <Field
          name="firstName"
          :value="firstName"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="text"
            placeholder="First Name"
            v-model:value="firstName"
            @update:value="handleChange"
            :errorValidate="errorMessage"
            :disabledInputField="!isEditProfile"
          />
        </Field>
      </div>
      <!-- Last Name -->
      <div class="mt-3 ms-4 me-4">
        Last Name<span class="required">*</span>
        <Field
          name="lastName"
          :value="lastName"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="text"
            placeholder="Last Name"
            v-model:value="lastName"
            @update:value="handleChange"
            :errorValidate="errorMessage"
            :disabledInputField="!isEditProfile"
          />
        </Field>
      </div>
      <!-- Phone number -->
      <div class="mt-3 ms-4 me-4">
        Mobile<span class="required">*</span>
        <phone-input-field
          v-model:phoneNumber="enteredPhoneNumber"
          :defaultCountryCode="countryCode"
          :disabled="!isEditProfile"
          @country-code="updateCountryCode"
        />
      </div>
      <!-- Email  -->
      <div class="mt-3 ms-4 me-4">
        Email<span class="required">*</span>
        <Field
          name="email"
          :value="email"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="text"
            placeholder="Email"
            v-model:value.trim="email"
            @update:value="handleChange"
            :errorValidate="errorMessage"
            :disabledInputField="!isEditProfile"
          />
        </Field>
      </div>
      <!-- Sign up button -->
      <default-button
        title="Save"
        type="submit"
        class="mt-5 mb-3"
        :disabled="!isEditProfile"
      />
    </Form>
  </div>
</template>

<script>
import { Form, Field } from "vee-validate";
import * as yup from "yup";
import { mapGetters } from "vuex";
import { useToast } from "vue-toastification";

import BackButton from "@/components/button/BackButton.vue";
import AppBar from "@/components/AppBar.vue";
import ListHeader from "@/components/ListHeader.vue";
import DefaultInputField from "@/components/input/DefaultInputField.vue";
import DefaultButton from "@/components/button/DefaultButton.vue";
import SmallCircleButton from "@/components/button/SmallCircleButton.vue";
import PhoneInputField from "@/components/phoneInput/PhoneInputField.vue";
import { updateCustomerInfo } from "@/services/customer";
import { validationRules } from "@/validation/ValidationRules";
import { countries } from "@/common/constant/Countries.js";

export default {
  components: {
    Form,
    Field,
    ListHeader,
    BackButton,
    DefaultButton,
    SmallCircleButton,
    DefaultInputField,
    AppBar,
    PhoneInputField,
  },
  name: "MyProfile",
  data() {
    return {
      firstName: null,
      lastName: null,
      enteredPhoneNumber: null,
      countryCode: "65",
      email: null,
      isEditProfile: false,
    };
  },
  created() {
    this.userInfo();
  },
  computed: {
    phoneMobile() {
      return `+${this.countryCode}${this.enteredPhoneNumber ?? ""}`;
    },
    schema() {
      return yup.object({
        firstName: validationRules.name,
        lastName: validationRules.name,
        email: validationRules.email,
      });
    },
    ...mapGetters(["loginUserInfo"]),
  },
  methods: {
    async handleEditProfile() {
      const customerInfo = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phoneMobile: this.phoneMobile,
      };
      try {
        await updateCustomerInfo(customerInfo).then((res) => {
          this.$store.commit("getLoginUserInfo", res.data);
        });
        useToast().success("Update Successfully.");
        this.isEditProfile = false;
      } catch (error) {
        const errorMessage =
          error?.data?.message || error?.response?.data?.message || "";
        const phoneRegisteredMatch = errorMessage.match(
          /(?:WebUser\s+)?Phone number\s+(\S+)\s+has already registered/i
        );
        if (phoneRegisteredMatch) {
          useToast().error(
            `The phone number ${phoneRegisteredMatch[1]} is already linked to another account.`
          );
        } else {
          useToast().error(
            "There is some problem accessing the outlet, kindly contact the salon for assistance."
          );
        }
      }
    },
    toggleEditProfile() {
      this.isEditProfile = !this.isEditProfile;
      if (!this.isEditProfile) {
        this.userInfo();
        this.$refs.profileForm.resetForm();
      }
    },
    updateCountryCode(value) {
      this.countryCode = value;
    },
    splitPhoneMobile(phoneMobile) {
      const digits = (phoneMobile || "").replace(/^\+/, "");
      const phoneCodes = countries
        .map((c) => c.phone.toString())
        .sort((a, b) => b.length - a.length);
      const match = phoneCodes.find((code) => digits.startsWith(code));
      if (match) {
        return { countryCode: match, phoneNumber: digits.slice(match.length) };
      }
      return { countryCode: this.countryCode, phoneNumber: digits };
    },
    userInfo() {
      if (Object.keys(this.loginUserInfo).length !== 0) {
        this.firstName = this.loginUserInfo.firstName;
        this.lastName = this.loginUserInfo.lastName;
        const { countryCode, phoneNumber } = this.splitPhoneMobile(
          this.loginUserInfo.phoneMobile
        );
        this.countryCode = countryCode;
        this.enteredPhoneNumber = phoneNumber;
        this.email = this.loginUserInfo.customerDetail.email;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.required {
  color: #e32;
  display: inline;
}
</style>
